import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ImageProps } from 'next/image'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/src/utils/styles'
import { ChainImg } from './chain-img'
import { Chevron } from './icons/chevron'
import { Skeleton, type SkeletonProps } from './skeleton'
import { Spinner } from './spinner'
import TokenImg from './token-img'

interface LoadingProps {
  isLoading?: boolean
  skeletonProps?: SkeletonProps
}

/** `div` elements can't have `disabled` style but it may apply to future list row elements beyond `button` */
const listRowVariants = cva(
  'flex items-center w-full transition-colors justify-between disabled:opacity-50 disabled:pointer-events-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:-outline-offset-2',
  {
    variants: {
      size: {
        sm: 'py-2 px-3 gap-3 rounded-xl',
        md: 'py-2 px-4 gap-3 rounded-2xl',
        lg: 'p-4 gap-4 rounded-2xl',
      },
      variant: {
        default: 'hover:bg-surface1-alpha',
        ghost: 'relative p-0 before:absolute hover:before:bg-surface1',
      },
    },
    compoundVariants: [
      {
        variant: ['ghost'],
        size: 'sm',
        class: 'before:-inset-x-3 before:-inset-y-1.5 before:rounded-xl',
      },
      {
        variant: ['ghost'],
        size: 'md',
        class: 'before:-inset-x-4 before:-inset-y-3 before:rounded-2xl',
      },
      {
        variant: ['ghost'],
        size: 'lg',
        class: 'before:-inset-x-5 before:-inset-y-3 before:rounded-2xl',
      },
    ],
    defaultVariants: {
      size: 'lg',
      variant: 'default',
    },
  },
)

const ListRow = ({
  className,
  children,
  size,
  variant,
  ref,
  ...rest
}: ComponentProps<'div'> & VariantProps<typeof listRowVariants>) => {
  return (
    <div
      ref={ref}
      className={cn(
        listRowVariants({
          size,
          variant,
          className,
        }),
        'group',
      )}
      data-size={size}
      {...rest}
    >
      {children}
    </div>
  )
}

ListRow.displayName = 'ListRow'

export interface ListRowButtonProps
  extends ComponentProps<'button'>,
    VariantProps<typeof listRowVariants> {
  asChild?: boolean
}

const ListRowButton = ({
  className,
  children,
  size,
  variant,
  asChild,
  ref,
  ...rest
}: ListRowButtonProps) => {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      ref={ref}
      type='button'
      className={cn(
        listRowVariants({
          size,
          variant,
          className,
        }),
        'group',
      )}
      data-size={size}
      {...rest}
    >
      {children}
    </Comp>
  )
}

ListRowButton.displayName = 'ListRowButton'

interface ListRowImageProps
  extends Omit<ImageProps, 'src' | 'alt'>,
    Pick<ComponentProps<'div'>, 'ref'> {
  chainImageSrc?: string
  chainImageAlt?: string
  classes?: {
    container?: string
  }
  placeholderName?: string
  src?: string
  alt?: string
  chainId?: string
  ticker?: string
  pending?: boolean
}

const ListRowImage = ({
  chainImageSrc,
  chainImageAlt,
  classes,
  placeholderName,
  src,
  chainId,
  pending,
  ref,
  ...rest
}: ListRowImageProps) => {
  if (pending) {
    return (
      <div
        ref={ref}
        className={cn(
          'relative justify-center items-center flex group-data-[size=md]:size-10 group-data-[size=lg]:size-12',
          classes?.container,
        )}
      >
        <Spinner className='absolute inset-0 w-full h-full text-accent-primary' />
        <TokenImg
          src={src}
          alt={placeholderName || 'Token icon'}
          width={32}
          height={32}
          className='group-data-[size=md]:size-7 group-data-[size=lg]:size-9'
          {...rest}
        />
      </div>
    )
  }

  return (
    <div ref={ref} className={cn('relative', classes?.container)}>
      <TokenImg
        src={src}
        alt={placeholderName || 'Token icon'}
        width={32}
        height={32}
        className='group-data-[size=md]:size-10 group-data-[size=lg]:size-12'
        {...rest}
      />
      {chainId && chainImageSrc && chainImageAlt && (
        <div className='absolute bottom-0 right-0 p-[3.75px] flex items-center justify-center bg-surface1-100 size-5 rounded-[6px]'>
          <ChainImg
            chainId={chainId}
            chainImg={chainImageSrc}
            chainName={chainImageAlt}
            containerClassName='size-5 min-w-5'
            className='size-3'
          />
        </div>
      )}
    </div>
  )
}

ListRowImage.displayName = 'ListRowImage'

const ListRowSideName = ({
  children,
  className,
  isLoading,
  skeletonProps,
  ref,
  ...rest
}: ComponentProps<'span'> & LoadingProps) => {
  if (isLoading) {
    return (
      <Skeleton
        className={cn('w-16 h-4', skeletonProps?.className)}
        {...skeletonProps}
      />
    )
  }

  return (
    <span
      ref={ref}
      data-testid='name'
      className={cn(
        'font-semibold text-primary group-data-[size=sm]:text-sm',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  )
}

ListRowSideName.displayName = 'ListRowSideName'

const ListRowSideDescription = ({
  children,
  className,
  isLoading,
  skeletonProps,
  ref,
  ...rest
}: ComponentProps<'span'> & LoadingProps) => {
  if (isLoading) {
    return (
      <Skeleton
        className={cn('w-12 h-3', skeletonProps?.className)}
        {...skeletonProps}
      />
    )
  }

  return (
    <span
      ref={ref}
      data-testid='ticker'
      className={cn(
        'text-sm text-secondary group-data-[size=sm]:text-xs',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  )
}

ListRowSideDescription.displayName = 'ListRowSideDescription'

export interface ListRowSideProps extends ComponentProps<'div'> {
  showIndicator?: boolean
  customIndicator?: ReactNode
  containerClassName?: string
}

const ListRowSide = ({
  children,
  className,
  showIndicator,
  customIndicator,
  containerClassName,
  ref,
  ...rest
}: ListRowSideProps) => {
  return (
    <div className={cn('flex items-center gap-3', containerClassName)}>
      <div
        ref={ref}
        className={cn('flex flex-col gap-1 text-left', className)}
        {...rest}
      >
        {children}
      </div>
      {showIndicator &&
        (customIndicator ?? (
          <Chevron className='text-tertiary -rotate-90 size-4 group-data-[size=lg]:size-6' />
        ))}
    </div>
  )
}

ListRowSide.displayName = 'ListRowSide'

export {
  ListRow,
  ListRowButton,
  ListRowImage,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
}
